import { matchPath } from "react-router-dom";

export interface RouteParent {
	/** Route pattern as declared in App.tsx (with a leading slash). */
	pattern: string;
	/** Where the header Back button goes; `:param` segments are filled from the current URL. Null = browser history. */
	to: string | null;
	/** i18n key for the Back button label (the parent's name). */
	labelKey: string;
	/** Carry the current query string to the parent (filters shared by both pages). */
	keepSearch?: boolean;
}

/**
 * Logical parent of every detail / create / edit page, used by the header Back button so "Back"
 * always goes up one level in the app (e.g. purchase → Purchases list) instead of to whatever page
 * the browser visited last. Top-level pages have no entry and show no Back button.
 */
export const ROUTE_PARENTS: RouteParent[] = [
	{ pattern: "/users/new", to: "/management?type=users", labelKey: "navigation.employees" },
	{ pattern: "/users/:id", to: "/management?type=users", labelKey: "navigation.employees" },
	{ pattern: "/products/new", to: "/products", labelKey: "navigation.products" },
	{ pattern: "/products/:id", to: "/products", labelKey: "navigation.products" },
	{ pattern: "/recipes/product/:productId", to: "/products/:productId", labelKey: "common.back" },
	{ pattern: "/sales/new", to: "/sales", labelKey: "navigation.sales" },
	{ pattern: "/sales/:id", to: "/sales", labelKey: "navigation.sales" },
	{ pattern: "/quotations/new", to: "/sales?type=quotations", labelKey: "navigation.quotations" },
	{ pattern: "/quotations/:id", to: "/sales?type=quotations", labelKey: "navigation.quotations" },
	{ pattern: "/quotations/:id/edit", to: "/quotations/:id", labelKey: "common.back" },
	{ pattern: "/purchases/new", to: "/sales?type=purchases", labelKey: "navigation.purchases" },
	{ pattern: "/purchases/:id", to: "/sales?type=purchases", labelKey: "navigation.purchases" },
	{ pattern: "/purchases/:id/edit", to: "/purchases/:id", labelKey: "common.back" },
	{ pattern: "/invoices/:id", to: "/sales?type=invoices", labelKey: "navigation.invoices" },
	{ pattern: "/invoices/:id/edit", to: "/invoices/:id", labelKey: "common.back" },
	{ pattern: "/returns/new", to: "/sales?type=returns", labelKey: "navigation.returns" },
	{ pattern: "/returns/:id", to: "/sales?type=returns", labelKey: "navigation.returns" },
	{ pattern: "/returns/:id/edit", to: "/returns/:id", labelKey: "common.back" },
	{ pattern: "/pos/new", to: "/pos/list", labelKey: "navigation.pointsOfSale" },
	{ pattern: "/pos/:id", to: "/pos/list", labelKey: "navigation.pointsOfSale" },
	{ pattern: "/pos/:id/edit", to: "/pos/:id", labelKey: "common.back" },
	// Transfers have no list of their own; they are opened from a product, so go back in history.
	{ pattern: "/stock-transfers/:id", to: null, labelKey: "common.back" },
	{ pattern: "/notifications/:id", to: "/notifications", labelKey: "navigation.notificationInbox" },
	// Report filters live in the query string and apply on the reports home too, so Back keeps them.
	{ pattern: "/reports/:reportId", to: "/reports", labelKey: "navigation.reports", keepSearch: true },
];

/** Parent of the page at `pathname`, with `:params` resolved; undefined on top-level pages. */
export function getRouteParent(pathname: string, search = ""): { to: string | null; labelKey: string } | undefined {
	for (const parent of ROUTE_PARENTS) {
		const match = matchPath({ path: parent.pattern, end: true }, pathname);
		if (!match) continue;
		const to =
			parent.to === null
				? null
				: parent.to.replace(/:([A-Za-z]+)/g, (_, name: string) => match.params[name] ?? "") +
					(parent.keepSearch ? search : "");
		return { to, labelKey: parent.labelKey };
	}
	return undefined;
}
